import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { Select, Spin, Tag } from 'antd';
import { DataCrafterOutputVariant } from '../../types';
import './OutputVariantsSelector.less';

interface Props {
    label?: string;
    variants: DataCrafterOutputVariant[];
    isLoadingVariants: boolean;
    value?: string;
    onChange?: (variantId: string | undefined) => void;
    placeholder?: string;
    style?: React.CSSProperties;
    disabled?: boolean;
    allowClear?: boolean;
}

export const INITIAL_VARIANT_ID = 'initial_output_variant';

/**
 * Component for selecting output variants with search functionality
 *
 * @param label - Optional label to display for the selector (use Form.Item label instead when used in a form)
 * @param variants - List of output variants to display
 * @param isLoadingVariants - Whether the variants are currently loading
 * @param value - Currently selected variant ID
 * @param onChange - Function to call when selection changes
 * @param placeholder - Optional placeholder text
 * @param style - Optional additional styles
 * @param disabled - Whether the selector is disabled
 * @param allowClear - Whether to show clear button
 */
const OutputVariantsSelector: React.FC<Props> = ({
    label,
    variants,
    isLoadingVariants,
    value,
    onChange,
    placeholder = 'Select an output variant',
    style,
    disabled = false,
    allowClear = true
}) => {
    // Convert variants to options format for Select component
    const options = React.useMemo(() => {
        // Add initial variant to the beginning of the list
        const initialIsDefault = !variants.some(variant => variant.isDefault);
        const variantsWithInitial = [
            {
                variantId: INITIAL_VARIANT_ID,
                name: 'Initial',
                isDefault: initialIsDefault
            },
            ...variants
        ];

        return variantsWithInitial.map((variant, index) => ({
            value: variant.variantId || `variant-${index}`,
            label: (
                <div className="variant-option">
                    <span className="variant-name">{variant.name}</span>
                    {variant.isDefault && (
                        <Tag style={{ marginLeft: 8 }} color="blue" className="default-tag">
                            Default
                        </Tag>
                    )}
                </div>
            ),
            title: variant.name, // For search functionality
            searchText: variant.name // For filtering
        }));
    }, [variants]);

    // Handle selection change
    const handleChange = (variantId: string | undefined) => {
        if (onChange) {
            onChange(variantId);
        }
    };

    const selectComponent = (
        <Select
            showSearch
            allowClear={allowClear}
            placeholder={isLoadingVariants ? 'Loading variants...' : placeholder}
            optionFilterProp="searchText"
            onChange={handleChange}
            filterOption={(input, option) =>
                (option?.searchText as string)?.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
            value={value ? value : INITIAL_VARIANT_ID}
            loading={isLoadingVariants}
            notFoundContent={isLoadingVariants ? <Spin size="small" /> : 'No variants found'}
            disabled={disabled || isLoadingVariants}
            options={options}
            className="output-variants-selector"
            defaultValue={INITIAL_VARIANT_ID}
        />
    );

    // If a label is provided, wrap the select in a div with the label
    if (label) {
        return (
            <div className="output-variants-selector-container" style={style}>
                <div className="output-variants-selector-label">{label}</div>
                {selectComponent}
            </div>
        );
    }

    // Otherwise, just return the select component
    return selectComponent;
};

export default observer(OutputVariantsSelector);
